import { apiFetch } from './client';

const BASE_URL = '/api/ai-core';

export interface Explanation {
  explanation: string;
  cached: boolean;
  expiresAt: string; // ISO
}

export interface ExplainQuery {
  pluginId: string;
  entityType: string;
  entityId: string;
  field: string;
}

export const fetchExplanation = async (q: ExplainQuery): Promise<Explanation> => {
  const qs = new URLSearchParams(q as unknown as Record<string, string>).toString();
  return apiFetch<Explanation>(`${BASE_URL}/explain?${qs}`, { credentials: 'include' });
};

export interface InsightSignal {
  kind: 'risk' | 'ok' | 'info';
  label: string;
  detail?: string;
}

export interface InsightAction {
  label: string;
  intent: 'open_chat_with_prompt' | 'navigate';
  payload: string;
}

export interface InsightCard {
  title: string;
  summary: string;
  signals: InsightSignal[];
  suggestedActions: InsightAction[];
}

export interface InsightCardResponse {
  card: InsightCard | null;
  cached: boolean;
  generatedAt: string;
  model: string;
  tokensUsed: number;
  fallback?: 'rate-limited' | 'unavailable';
}

export const fetchInsightCard = async (q: {
  pluginId: string;
  entityType: string;
  entityId: string;
  regenerate?: boolean;
  force?: boolean;
}): Promise<InsightCardResponse> => {
  const qs = new URLSearchParams({
    pluginId: q.pluginId,
    entityType: q.entityType,
    entityId: q.entityId,
    ...(q.regenerate ? { regenerate: '1' } : {}),
    ...(q.force ? { force: '1' } : {}),
  }).toString();
  return apiFetch<InsightCardResponse>(`${BASE_URL}/entity-insight-card?${qs}`, { credentials: 'include' });
};

export type InsightStatus = 'active' | 'dismissed' | 'resolved';
export type InsightSeverity = 'info' | 'warning' | 'critical';

export interface InsightEvidence {
  label: string;
  value: string | number;
  source?: { tool: string; ref?: string };
}

export interface InsightScope {
  pluginId: string;
  entityType?: string;
  entityId?: string;
  roleHint?: 'exec' | 'warehouse' | 'purchasing' | 'csr' | 'sales-ops';
}

export interface AiInsight {
  id: string;
  companyId: string;
  scope: InsightScope;
  kind: string;
  severity: InsightSeverity;
  title: string;
  summary: string;
  evidence: InsightEvidence[];
  computedAt: string; // serialized over HTTP
  validUntil?: string;
  status: InsightStatus;
  dismissedBy?: { userId: string; at: string; reason?: string };
  resolvedAt?: string;
  resolvedReason?: 'rule_cleared' | 'entity_deleted' | 'manual';
  ruleId: string;
  ruleVersion: number;
}

export interface FetchInsightsQuery {
  status?: InsightStatus;
  pluginId?: string;
  severity?: InsightSeverity;
  cursor?: string;
  limit?: number;
}

export interface InsightsListResponse {
  items: AiInsight[];
  nextCursor: string | null;
}

export const fetchCompanyInsights = (q: FetchInsightsQuery = {}): Promise<InsightsListResponse> => {
  const params = new URLSearchParams();
  if (q.status) params.set('status', q.status);
  if (q.pluginId) params.set('pluginId', q.pluginId);
  if (q.severity) params.set('severity', q.severity);
  if (q.cursor) params.set('cursor', q.cursor);
  if (q.limit) params.set('limit', String(q.limit));
  return apiFetch<InsightsListResponse>(`${BASE_URL}/insights?${params.toString()}`, {
    credentials: 'include',
  });
};

/** Durations offered by "Remind me later"; must match the server's allow-list. */
export const SNOOZE_DAY_OPTIONS = [1, 2, 3, 7] as const;
export type SnoozeDays = (typeof SNOOZE_DAY_OPTIONS)[number];

/**
 * Hides the insight from THIS user's feed for `days`. Colleagues are unaffected
 * and the insight itself stays active, unlike dismiss.
 */
export const snoozeInsight = (id: string, days: SnoozeDays): Promise<{ snoozedUntil: string }> =>
  apiFetch<{ snoozedUntil: string }>(`${BASE_URL}/insights/${encodeURIComponent(id)}/snooze`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ days }),
  });

export const dismissInsight = (
  id: string,
  reason?: string
): Promise<{ alreadyDismissed?: boolean; insight?: AiInsight }> =>
  apiFetch<{ alreadyDismissed?: boolean; insight?: AiInsight }>(
    `${BASE_URL}/insights/${encodeURIComponent(id)}/dismiss`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(reason ? { reason } : {}),
    }
  );
